import {
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import {usePrevious} from '../../../../../../common/shared/hooks/usePrevious';
import {preventDefaultEvent} from '../../../../../../common/shared/utils/preventDefaultEvent.function';
import {stopPropagationEvent} from '../../../../../../common/shared/utils/stopPropagationEvent.function';
import {twMerge} from '../../../../../../common/twMerge';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {Icon} from '../../../../../../design-system/atoms/Icon/Icon';
import {IconType} from '../../../../../../design-system/atoms/Icon/IconType.enum';
import {TalkViewerFinishIndicator} from '../../../../../../design-system/organisms/TalkViewerFinishIndicator/TalkViewerFinishIndicator';
import {MOBILE_HEADER_HEIGHT_PX} from '../../../../../../header/constants';
import {LOG_CLICK_EVENT} from '../../../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../../../logging/putTiaraObjectProps';
import {useViewerInfoQuery} from '../../../../../../wrapper/viewerWrapper';
import {events} from '../constant/talkViewerEvents';
import {VIEWER_MENU_FIRST_EXPOSE_TIME} from '../constant/viewerConstant';
import {setIsViewerMenuExposed} from '../state/viewerMenuSlice';
import {AutoTapType} from '../type/AutoTapType.enum';
import {ViewingMode} from '../type/ViewingMode.enum';
import {ZIndex} from '../type/ZIndex.enum';
import {scrollToWindowBottommost} from '../util/scrollToWindowBottommost';
import {selectCanShowViewerEnd} from '../util/selector';
import {TalkViewerAutoTapContainer} from './TalkViewerAutoTapContainer';
import {TalkViewerBgmContainer} from './TalkViewerBgmContainer';
import {TalkViewerDisplayAdContainer} from './TalkViewerDisplayAdContainer';
import {TalkViewerEventHandleContainer} from './TalkViewerEventHandleContainer';
import {TalkViewerFullscreenContainer} from './TalkViewerFullscreenContainer';
import {TalkViewerGlobalStyleContainer} from './TalkViewerGlobalStyleContainer';
import {TalkViewerProgressSyncContainer} from './TalkViewerProgressSyncContainer';
import {TalkViewerSceneContainer} from './TalkViewerSceneContainer';
import {TalkViewerSeekBarContainer} from './TalkViewerSeekBarContainer';

interface TalkViewerContainerProps {
  seriesId: number;
  productId: number;
  children?: ReactNode;
}

export function TalkViewerContainer({
  seriesId,
  productId,
  children,
}: TalkViewerContainerProps) {
  const {
    isViewerMenuOpened,
    talkMap,
    muted,
    isFullscreen,
    productInfo,
    viewerMenuHeight,
    isViewerEndExposed,
    viewingMode,
    autoTap,
    isPreview,
  } = useTypedSelector(state => ({
    isViewerMenuOpened: state.viewerMenu.isExposed,
    talkMap: state.talkViewer.talkMap,
    muted: state.talkViewer.internalSettings.muted,
    isFullscreen: !!state.talkViewer.fullscreen,
    productInfo: state.talkViewer.productInfoMap[productId],
    viewerMenuHeight: state.talkViewer.settings.viewerMenuHeight,
    isViewerEndExposed: state.viewerEnd.isExposed,
    viewingMode: state.talkViewer.settings.viewingMode,
    autoTap: state.talkViewer.internalSettings.autoTap,
    isPreview: state.talkViewer.isPreview,
  }));
  const viewerInfoData = useViewerInfoQuery({seriesId, productId}).data;
  const canShowViewerEnd = useTypedSelector(state =>
    selectCanShowViewerEnd(state, viewerInfoData)
  );

  const hasVideoOrAudio = useMemo(() => {
    return Object.values(talkMap).some(
      talk =>
        talk.productId === productId &&
        talk.media?.source?.url &&
        // 비디오,오디오는 mp3, mp4확장자 외에 카톡소설 저작툴에서 넣는 것 자체가 불가능
        (talk.media.source.url.includes('.mp3') ||
          talk.media.source.url.includes('.mp4'))
    );
  }, [talkMap, productId]);

  const dispatch = useTypedDispatch();
  const toggleMuted = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      function handler() {
        dispatch(events.mutedToggleTapPresssed());
      }
      LOG_CLICK_EVENT('음소거_클릭', e, handler);
    },
    [dispatch]
  );

  const prevIsViewerEndExposed = usePrevious(isViewerEndExposed);
  useLayoutEffect(() => {
    if (
      prevIsViewerEndExposed === undefined ||
      prevIsViewerEndExposed === isViewerEndExposed
    ) {
      return;
    }
    // 뷰어엔드가 열리면 스크롤을 상단으로 이동시켜준다.
    if (isViewerEndExposed) {
      scrollTo(0, 1);
    }
    // 뷰어엔드가 닫히면 스크롤 맨 아래로 내린다.
    else {
      scrollToWindowBottommost();
    }
  }, [isViewerEndExposed, prevIsViewerEndExposed]);

  const [canShowDa, setCanShowDa] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      // 최초 뷰어메뉴를 내리기 전까진 타이밍이슈로 DA노출이 불가능한 상황이 올 수가 있어 DA노출을 막습니다.
      setCanShowDa(true);
      dispatch(setIsViewerMenuExposed({isExposed: false}));
    }, VIEWER_MENU_FIRST_EXPOSE_TIME);

    return () => {
      clearTimeout(timer);
    };
  }, [dispatch]);

  const {isWebView} = requestContext.requestInfo.display;
  useEffect(() => {
    function visibilityChangeHandler() {
      if (isWebView) {
        return;
      }
      // 브라우저가 백그라운드로 내려간 경우 document가 hidden상태이므로 이 때에 뷰어를 일시정지 시켜준다.
      if (document.hidden) {
        dispatch(events.viewerPaused());
      }
    }
    document.addEventListener('visibilitychange', visibilityChangeHandler);
    return () =>
      document.removeEventListener('visibilitychange', visibilityChangeHandler);
  }, [isWebView, dispatch]);

  useEffect(() => {
    document.addEventListener('contextmenu', preventDefaultEvent);
    return () =>
      document.removeEventListener('contextmenu', preventDefaultEvent);
  }, []);

  const isEndOfTalkReached =
    productInfo.progress.totalTalkCount ===
    productInfo.progress.visitedTalkCount;

  return (
    <TalkViewerEventHandleContainer
      productId={productId}
      sceneId={productInfo?.currentSceneId}
    >
      <TalkViewerGlobalStyleContainer />
      <TalkViewerFullscreenContainer />
      {canShowDa && (
        <TalkViewerDisplayAdContainer
          seriesId={seriesId}
          productId={productId}
        />
      )}
      {productInfo?.currentSceneId && (
        <TalkViewerSceneContainer sceneId={productInfo.currentSceneId} />
      )}
      <TalkViewerAutoTapContainer />
      {isViewerMenuOpened && !isWebView && !isPreview && (
        <div
          className={twMerge(
            'fixed inset-x-0 z-150 pr-safe pb-safe pl-safe',
            // 특정 iOS 사파리 브라우저에서 틈새가 발생하는 이슈를 위한 속성입니다. 1px 큰 요소를 만들어 배경으로 씁니다
            'before:absolute before:top-0 before:left-0 before:z-[-1] before:h-26pxr before:w-full before:bg-theme-bg-base'
          )}
          style={{
            bottom: MOBILE_HEADER_HEIGHT_PX,
          }}
          onClick={stopPropagationEvent}
        >
          <TalkViewerSeekBarContainer productId={productId} />
        </div>
      )}
      <div
        className={twMerge(
          'fixed bottom-0 left-0 mx-auto w-full',
          isViewerMenuOpened && 'invisible',
          isFullscreen && 'pointer-events-none hidden'
        )}
        style={{
          zIndex: ZIndex.Talk + 1,
          marginBottom: viewerMenuHeight.safeAreaBottom || 0,
        }}
      >
        <TalkViewerBgmContainer />
        {hasVideoOrAudio && !isViewerEndExposed && (
          <div
            className="absolute right-0 bottom-0 r-safe"
            {...putTiaraObjectProps({
              click: {layer1: '뷰어메뉴', layer2: '기타'},
            })}
          >
            <Icon
              className="m-11tvs mr-39tvs box-content h-64tvs w-64tvs p-28tvs pr-0"
              onClick={toggleMuted}
              type={muted ? IconType.SoundOff : IconType.SoundOn}
            />
          </div>
        )}
      </div>
      {children}
      {!isViewerEndExposed &&
        isEndOfTalkReached &&
        autoTap !== AutoTapType.Play &&
        viewingMode === ViewingMode.Tap &&
        // 풀스린이 떠 있을 때는 마지막 회차버튼 노출 X ( 전체이미지의 애니메이션 때문에, 전체이미지가 뜨면서 마지막 회차버튼이 보여서 들어간 부분. 전체동영상은 애니메이션이 없어 이 코드에 크게 영향받지 않는다. )
        !isFullscreen && (
          <TalkViewerFinishIndicator canShowViewerEnd={canShowViewerEnd} />
        )}
      {seriesId && !isPreview && (
        <TalkViewerProgressSyncContainer
          seriesId={seriesId}
          productId={productId}
        />
      )}
    </TalkViewerEventHandleContainer>
  );
}
